import React, { useState, useEffect } from 'react';
import { 
  Gamepad2, Play, Pause, Square, 
  X, ArrowRight, Volume2 
} from 'lucide-react';
import useDragToScroll from '../../hooks/useDragToScroll';
import sfx from '../../utils/soundEffects';
import { speakFrench, stopSpeaking, pauseSpeaking, resumeSpeaking, hasUserInteracted } from '../../utils/voiceHelper';

const WELCOME_SPEECH_TEXT = `
Bienvenue sur le simulateur MonAlerte ! 
Ce mini-jeu d'environ 15 minutes, en vitesse x4, reproduit une agression urbaine en plein cœur d'un quartier avec ses rues et ses bâtiments.
Votre objectif est de faire évoluer votre personnage, de parcourir la ville et d'échapper au maximum aux agressions.
Au début, vous ne pourrez pas faire grand-chose. Mais au fur et à mesure que vous gagnez de l'expérience, vous débloquerez les vraies fonctionnalités de l'application MonAlerte : touche raccourcis d'urgence, montre connectée et alertes instantanées.
Vous gagnerez également des points de vitalité pour ajouter des citoyens dans la ville et des citoyens protecteurs. Plus il y a de citoyens, plus il y a de protecteurs et plus vous réduirez l'agression.
Enfin, plus vous accumulez de preuves vidéo et audio, plus il sera possible d'identifier formellement votre agresseur, de le faire condamner et de réduire les frais de justice.
Bonne chance, citoyen !
`.trim();

const WelcomeGameModal = ({
  isOpen,
  onClose,
  onStartGame,
}) => {
  const containerRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });
  const cardRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });

  // État Narration Vocale
  const [ttsState, setTtsState] = useState('idle'); // 'idle' | 'playing' | 'paused'

  // Démarrage automatique de la narration dès l'ouverture de la popup
  useEffect(() => {
    if (!isOpen) {
      stopSpeaking();
      setTtsState('idle');
      return;
    }

    // Narration en synthèse vocale (texte toujours à jour) — le fichier audio HD pré-enregistré
    // est désactivé tant qu'il n'a pas été régénéré avec le texte courant.
    const startNarration = () => {
      playWebSpeechFallback();
    };

    // On ne tente un démarrage automatique (son + narration) que si un geste utilisateur a déjà eu
    // lieu sur la page : sinon le navigateur bloque silencieusement l'audio et peut coincer la
    // synthèse vocale. Sans geste préalable, on attend le premier clic/touche pour démarrer.
    if (hasUserInteracted()) {
      startNarration();
    }

    const unlockNarration = () => {
      if (!('speechSynthesis' in window) || (!window.speechSynthesis.speaking && !window.speechSynthesis.pending)) {
        startNarration();
      }
      window.removeEventListener('pointerdown', unlockNarration, true);
      window.removeEventListener('keydown', unlockNarration, true);
      window.removeEventListener('touchstart', unlockNarration, true);
      window.removeEventListener('click', unlockNarration, true);
      document.removeEventListener('click', unlockNarration, true);
    };

    window.addEventListener('pointerdown', unlockNarration, { capture: true, passive: true });
    window.addEventListener('keydown', unlockNarration, { capture: true, passive: true });
    window.addEventListener('touchstart', unlockNarration, { capture: true, passive: true });
    window.addEventListener('click', unlockNarration, { capture: true, passive: true });
    document.addEventListener('click', unlockNarration, { capture: true, passive: true });

    return () => {
      window.removeEventListener('pointerdown', unlockNarration, true);
      window.removeEventListener('keydown', unlockNarration, true);
      window.removeEventListener('touchstart', unlockNarration, true);
      window.removeEventListener('click', unlockNarration, true);
      document.removeEventListener('click', unlockNarration, true);
      stopSpeaking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const playWebSpeechFallback = () => {
    speakFrench(WELCOME_SPEECH_TEXT, {
      onStart: () => setTtsState('playing'),
      onEnd: () => setTtsState('idle'),
      onError: () => setTtsState('idle'),
    });
  };

  const handlePlayTTS = () => {
    sfx.playClick();
    if (ttsState === 'paused') {
      resumeSpeaking();
      setTtsState('playing');
    } else {
      playWebSpeechFallback();
    }
  };

  const handlePauseTTS = () => {
    sfx.playClick();
    if (ttsState === 'playing') {
      pauseSpeaking();
      setTtsState('paused');
    }
  };

  const handleStopTTS = () => {
    sfx.playClick();
    stopSpeaking();
    setTtsState('idle');
  };

  const handleStartAdventure = () => {
    sfx.playClick();
    handleStopTTS();
    if (onClose) onClose();
  };

  const handleClose = () => {
    sfx.playClick();
    handleStopTTS();
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className="welcome-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(3, 7, 18, 0.88)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1110,
        borderRadius: '16px',
        padding: '10px',
        overflow: 'hidden',
        boxSizing: 'border-box',
        animation: 'fadeIn 0.25s ease',
      }}
    >
      <style>{`
        @keyframes pulseGlow {
          0% { box-shadow: 0 0 15px rgba(242, 184, 7, 0.4); }
          50% { box-shadow: 0 0 35px rgba(242, 184, 7, 0.8); }
          100% { box-shadow: 0 0 15px rgba(242, 184, 7, 0.4); }
        }
        @keyframes soundWave {
          0%, 100% { height: 4px; }
          50% { height: 18px; }
        }
        @keyframes pulseButton {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(56, 189, 248, 0.7); }
          70% { transform: scale(1.03); box-shadow: 0 0 0 10px rgba(56, 189, 248, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(56, 189, 248, 0); }
        }
        @media (max-width: 860px) {
          .welcome-modal-overlay {
            align-items: flex-start !important;
            padding-top: 6px !important;
          }
        }
      `}</style>

      <div
        ref={cardRef}
        style={{
          background: 'linear-gradient(165deg, rgba(15, 34, 64, 0.98) 0%, rgba(6, 13, 24, 0.99) 100%)',
          border: '2px solid var(--mga-gold)',
          borderRadius: '16px',
          maxWidth: '820px',
          width: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: '20px 24px',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.95), 0 0 30px rgba(242, 184, 7, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          position: 'relative',
          animation: 'slideUpDuel 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        }}
      >
        {/* Bouton Fermer */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: 'none',
            color: '#cbd5e1',
            borderRadius: '50%',
            width: '34px',
            height: '34px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <X size={18} />
        </button>

        {/* EN-TÊTE DU JEU */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', borderBottom: '1.5px solid rgba(242, 184, 7, 0.25)', paddingBottom: '14px' }}>
          <div style={{
            width: '54px',
            height: '54px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, rgba(242, 184, 7, 0.25) 0%, rgba(217, 119, 6, 0.4) 100%)',
            border: '1.5px solid var(--mga-gold)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.8rem',
            boxShadow: '0 4px 15px rgba(242, 184, 7, 0.35)',
            flexShrink: 0,
          }}>
            🎮
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#ffffff', fontWeight: 900, letterSpacing: '0.3px' }}>
                Bienvenue sur le Simulateur <span style={{ color: 'var(--mga-gold)' }}>MonAlerte</span>
              </h2>
              <span style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                padding: '2px 8px',
                borderRadius: '6px',
                fontSize: '0.66rem',
                fontWeight: 800,
                textTransform: 'uppercase',
              }}>
                ⏳ Session ~15 min (vitesse x4)
              </span>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', color: '#cbd5e1', lineHeight: 1.4 }}>
              Serious Game interactif de mise en situation urbaine et d'apprentissage des réflexes de protection citoyenne.
            </p>
          </div>
        </div>

        {/* BARRE NARRATION VOCALE HD */}
        <div style={{
          background: 'rgba(5, 11, 20, 0.85)',
          border: '1.5px solid rgba(56, 189, 248, 0.35)',
          borderRadius: '12px',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', color: '#38bdf8', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Volume2 size={15} color="#38bdf8" />
              🗣️ Narration Vocale :
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {ttsState !== 'playing' ? (
                <button
                  type="button"
                  onClick={handlePlayTTS}
                  style={{
                    background: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)',
                    color: '#ffffff',
                    border: 'none',
                    padding: '7px 14px',
                    borderRadius: '8px',
                    fontSize: '0.76rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: '0 2px 10px rgba(56, 189, 248, 0.3)',
                  }}
                >
                  <Play size={13} fill="currentColor" /> {ttsState === 'paused' ? 'Reprendre' : 'Écouter'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePauseTTS}
                  style={{
                    background: 'rgba(245, 158, 11, 0.25)',
                    color: '#ffd873',
                    border: '1px solid #f59e0b',
                    padding: '7px 14px',
                    borderRadius: '8px',
                    fontSize: '0.76rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <Pause size={13} /> Pause
                </button>
              )}

              {ttsState !== 'idle' && (
                <button
                  type="button"
                  onClick={handleStopTTS}
                  style={{
                    background: 'rgba(239, 68, 68, 0.2)',
                    color: '#fca5a5',
                    border: '1px solid #ef4444',
                    padding: '7px 12px',
                    borderRadius: '8px',
                    fontSize: '0.76rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Square size={12} fill="currentColor" /> Stop
                </button>
              )}
            </div>

            {/* Onde audio animée */}
            {ttsState === 'playing' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px', height: '20px', marginLeft: '6px' }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <span
                    key={i}
                    style={{
                      width: '3px',
                      background: '#38bdf8',
                      borderRadius: '2px',
                      animation: `soundWave 0.6s infinite ease-in-out ${i * 0.12}s`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <span style={{ fontSize: '0.70rem', color: '#94a3b8' }}>
            🔊 Voix Studio HD
          </span>
        </div>

        {/* CONTENU PÉDAGOGIQUE DU SERIOUS GAME EN 4 CARTES */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '10px',
        }}>
          {/* Carte 1 : Évolution & XP */}
          <div style={{
            background: 'rgba(15, 39, 74, 0.6)',
            border: '1.2px solid rgba(242, 184, 7, 0.35)',
            borderRadius: '12px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.2rem' }}>✨</span>
              <strong style={{ color: 'var(--mga-gold)', fontSize: '0.84rem' }}>
                Progression RPG & XP
              </strong>
            </div>
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#cbd5e1', lineHeight: 1.45 }}>
              Au départ, vos réflexes sont basiques. En gagnant de l'<strong>XP</strong>, débloquez les fonctionnalités réelles de l'app : <strong>Montre Connectée, Touche Raccourcis (Power/Volume)</strong>.
            </p>
          </div>

          {/* Carte 2 : PV & Protecteurs */}
          <div style={{
            background: 'rgba(15, 39, 74, 0.6)',
            border: '1.2px solid rgba(239, 68, 68, 0.35)',
            borderRadius: '12px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.2rem' }}>💖</span>
              <strong style={{ color: '#f87171', fontSize: '0.84rem' }}>
                Points de Vitalité (PV)
              </strong>
            </div>
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#cbd5e1', lineHeight: 1.45 }}>
              Utilisez vos <strong>PV</strong> pour ajouter des <strong>👥 Citoyens</strong> et activer des <strong>🛡️ Citoyens Protecteurs</strong> (option réelle de MonAlerte). Plus il y a de monde, plus l'agresseur hésite et recule !
            </p>
          </div>

          {/* Carte 3 : Situations & Déclenchement */}
          <div style={{
            background: 'rgba(15, 39, 74, 0.6)',
            border: '1.2px solid rgba(56, 189, 248, 0.35)',
            borderRadius: '12px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.2rem' }}>📱</span>
              <strong style={{ color: '#38bdf8', fontSize: '0.84rem' }}>
                Flexibilité Tactique
              </strong>
            </div>
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#cbd5e1', lineHeight: 1.45 }}>
              Téléphone en poche, dans le sac ou en main : multipliez vos méthodes de SOS pour faire face à tous les profils d'agresseurs sans risquer de vous faire confisquer votre mobile.
            </p>
          </div>

          {/* Carte 4 : Preuves & Justice */}
          <div style={{
            background: 'rgba(15, 39, 74, 0.6)',
            border: '1.2px solid rgba(16, 185, 129, 0.35)',
            borderRadius: '12px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.2rem' }}>⚖️</span>
              <strong style={{ color: '#86efac', fontSize: '0.84rem' }}>
                Preuves & Condamnation
              </strong>
            </div>
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#cbd5e1', lineHeight: 1.45 }}>
              Plus vous collectez de preuves vidéo HD et audio en direct, plus l'agresseur est <strong>formellement identifié et arrêté</strong>, réduisant les litiges et les frais de justice.
            </p>
          </div>
        </div>

        {/* BOUTON D'ACTION PRINCIPAL */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginTop: '6px' }}>
          <span style={{ fontSize: '0.70rem', color: '#94a3b8' }}>
            💡 Vous pouvez réouvrir ce guide à tout moment via le bouton <strong>🎮 Guide & Histoire</strong>.
          </span>

          <button
            type="button"
            onClick={handleStartAdventure}
            style={{
              background: 'linear-gradient(135deg, #f2b807 0%, #d97706 100%)',
              color: '#000000',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '12px',
              fontWeight: 900,
              fontSize: '0.92rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 20px rgba(242, 184, 7, 0.45)',
              letterSpacing: '0.3px',
              transition: 'transform 0.15s ease',
            }}
          >
            <Gamepad2 size={20} />
            <span>🚀 Accéder au Simulateur</span>
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeGameModal;
